import { createUserCampaign } from "@/lib/campaignsClient";
import { getRootNote, updateNote } from "@/lib/campaignNoteClient";
import type { UserCampaignRecord } from "@west-marches/shared";
import type { MapDocument } from "@west-marches/shared";
import type { PendingCloudOnboarding } from "@/state/pendingCloudOnboarding";

export interface FinalizePendingResult {
  campaign: UserCampaignRecord;
}

// Module-level in-flight guard. The finalize trigger lives inside the
// `HomeClient` component, but route changes during signup (e.g. navigating
// from a stale `/campaign/<shortId>` URL back to `/`) can briefly mount two
// HomeClient instances in sequence. Each instance has its own
// `useRef`-based dedup, so without a shared guard both would create a
// duplicate campaign for the same pending payload.
//
// The key is `pending.createdAt` — a unique-per-signup ISO timestamp captured
// when the user submitted the onboarding form.
const inflight = new Map<string, Promise<FinalizePendingResult>>();

export async function finalizePendingCloudOnboarding({
  pending,
  document,
  updateDisplayName,
}: {
  pending: PendingCloudOnboarding;
  document: MapDocument;
  updateDisplayName: (displayName: string) => Promise<void>;
}): Promise<FinalizePendingResult> {
  const key = pending.createdAt;
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }

  const work = (async () => {
    await updateDisplayName(pending.displayName);
    const campaign = await createUserCampaign({
      name: pending.campaignName,
      document,
    });

    if (pending.anonymousNotebookContent) {
      const rootNote = await getRootNote(campaign.id, "gm");
      await updateNote(campaign.id, rootNote.id, {
        content: pending.anonymousNotebookContent,
      });
    }

    return { campaign };
  })();

  inflight.set(key, work);
  try {
    return await work;
  } finally {
    inflight.delete(key);
  }
}
